import type { IHttpClient } from "@/core/interfaces";
import { Registry } from "@/shared/infra/dependency-injection/registry";

/**
 * Contrato da HU 2040 (técnico), exposto pelo hub-bff — privilégio `hub.equipe:editar`,
 * verificado pela API. Os corpos seguem o contrato das operações implementadas
 * no hub-bff (task 2435) e no hub-core (task 2438).
 */
export const CONFLITO_ALOCACAO_PATHS = {
  CONFLITOS_IGNORADOS: "/api/recursos/alocacoes/profissionais/conflitos-ignorados",
} as const;

export function buildRemanejamentoPath(alocacaoId: string): string {
  return `/api/recursos/alocacoes/profissionais/${encodeURIComponent(alocacaoId)}/remanejamento`;
}

export interface RemanejarProfissionalParams {
  /** Alocação do profissional em conflito na obra escolhida. */
  alocacaoId: string;
  profissionalSubstitutoId: string;
  justificativa: string;
  /** Disponibilidade do substituto que o usuário viu ao selecioná-lo (o core valida no envio). */
  disponibilidadeVisualizada?: DisponibilidadeVisualizada;
  signal?: AbortSignal;
}

export type DisponibilidadeVisualizada = "DISPONIVEL" | "INDISPONIVEL";

export interface IgnorarConflitoParams {
  profissionalId: string;
  /** Alocações sobrepostas que formam o conflito (no mínimo 2). */
  alocacaoIds: string[];
  justificativa: string;
  signal?: AbortSignal;
}

function getHttpClient(): IHttpClient {
  return Registry.getInstance().inject<IHttpClient>("HttpClient");
}

/** RN03/RN04 — troca o profissional da alocação (só o `usuario_id` muda no core). */
export async function remanejarProfissional({
  alocacaoId,
  profissionalSubstitutoId,
  justificativa,
  disponibilidadeVisualizada,
  signal,
}: RemanejarProfissionalParams): Promise<void> {
  await getHttpClient().patch<unknown>(
    buildRemanejamentoPath(alocacaoId),
    {
      profissionalSubstitutoId,
      justificativa,
      ...(disponibilidadeVisualizada ? { disponibilidadeVisualizada } : {}),
    },
    { signal },
  );
}

/** RN06/RN07 — registra o conflito como tratado, mantendo as alocações. */
export async function ignorarConflito({
  profissionalId,
  alocacaoIds,
  justificativa,
  signal,
}: IgnorarConflitoParams): Promise<void> {
  await getHttpClient().post<unknown>(
    CONFLITO_ALOCACAO_PATHS.CONFLITOS_IGNORADOS,
    { profissionalId, alocacaoIds, justificativa },
    { signal },
  );
}
